import React, { useEffect, useMemo, useState } from 'react'
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, LinearProgress } from '@mui/material';
import { makeStyles } from '@mui/styles'
import { useNavigate } from 'react-router-dom';

import { DebouncedTextField } from 'components/CustomFields';
import { SimpleSelect } from 'components/CustomSelectFields';
import { getBatchHighlights } from '../../services/analyticsV2';
import { exportToCsv } from 'utils';
import { BatchHighlightingOptions, DurationOptions, InterviewVariantTypes } from './constant';
import CustomButton from 'components/CustomButtons';
import InfoIconWithTooltip from 'components/InfoIconWithTooltip';
import Pagination from 'components/Pagination';
import TextField from 'components/CustomFields';

const ScoreCategoryOptions = [
  { _id: 'overall', name: 'Overall' },
  { _id: 'communication', name: 'Communication' },
  { _id: 'content', name: 'Content' }
];

const CodingScoreCategoryOptions = [
  { _id: '4c', name: '4C' },
  { _id: 'communication', name: 'Communication' }
];

const useStyles = makeStyles((theme) => ({
    container : {
        display : 'flex',
        flexDirection : 'column',
        gap : theme.spacing(6)
    },
    tableContainer : {
        display : 'flex',
        flexDirection : 'column',
        gap : theme.spacing(3)
    }
}))

const BatchHighlightingEmptyState = ({ batchId, interviewVariantType = InterviewVariantTypes.VERBAL_MOCK }) => {
  const classes = useStyles();
  
  return (
    <Box className={classes.container}>
      <Box display='flex' justifyContent='space-between' width='100%'>
        <Box display='flex' flexDirection='column' gap='4px'>
          <Typography variant='h4-medium'>
            Batch highlights
          </Typography>
          <Typography variant='h6-medium' color='neutral.clr-600'>
            Track students activities across the batch
          </Typography>
        </Box>
        <Box display='flex' gap='16px' alignItems='flex-end'>
          <SimpleSelect
            options={BatchHighlightingOptions}
            value={BatchHighlightingOptions[0]?._id || ''}
            onSelect={() => {}}
            sx={{ width: '360px' }}
            label='View batch highlight by'
            disabled
          />
          <CustomButton size='medium' disabled>
            Export
          </CustomButton>
        </Box>      
      </Box>
      <Box className={classes.tableContainer}>
        <Typography variant='h5-medium' color='primary.clr-500'>
          N/A
        </Typography>
        <Box display='flex' justifyContent='space-between' width='100%'>
          <Box display='flex' gap='12px' alignItems='flex-end'>
            <SimpleSelect
              options={DurationOptions}
              value={DurationOptions[0]?._id || ''}
              onSelect={() => {}}
              sx={{ width: '180px' }}
              label='Time duration'
              disabled
            />
            <DebouncedTextField
              placeholder="Search..."
              sx={{ width: '220px' }}
              onChange={() => {}}
              disabled
            />
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap="4px">
              <Typography variant="body01-bold">Minimal attempts</Typography>
              <TextField
                type="number"
                placeholder="Enter attempts (1-50)"
                sx={{ width: '180px' }}
                size="small"
                disabled
              />
            </Box>
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap="4px">
              <Typography variant="body01-bold">Min Score</Typography>
              <TextField
                type="number"
                placeholder="Score (0-100)"
                sx={{ width: '180px' }}
                size="small"
                disabled
              />
            </Box>
            <SimpleSelect
              options={ScoreCategoryOptions}
              value={ScoreCategoryOptions[0]?._id || ''}
              onSelect={() => {}}
              sx={{ width: '180px' }}
              label='Score By'
              disabled
            />
          </Box>
        </Box>
        
        <TableContainer component={Paper} sx={{ width: '100%' }}>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={240}>
            <Box mb={2}>
              <img
                src='https://assets.languify.in/images/no_data.png'
                alt='no data'
                width={151}
                height={97}
              />
            </Box>
            <Typography variant="h6-medium">No data available</Typography>
          </Box>
        </TableContainer>
      </Box>
    </Box>
  );
};

const HeaderControls = ({ selectedHighlightOption, onHighlightOptionChange, onExport, isExportDisabled }) => (
  <Box display='flex' justifyContent='space-between' width='100%'>
    <Box display='flex' flexDirection='column' gap='4px'>
      <Typography variant='h4-medium'>
        Batch highlights
      </Typography>
      <Typography variant='h6-medium' color='neutral.clr-600'>
        Track students activities across the batch
      </Typography>
    </Box>
    <Box display='flex' gap='16px' alignItems='flex-end'>
      <SimpleSelect
        options={BatchHighlightingOptions}
        value={selectedHighlightOption}
        onSelect={onHighlightOptionChange}
        sx={{ width: '360px' }}
        label='View batch highlight by'
      />
      <CustomButton size='medium' onClick={onExport} disabled={isExportDisabled}>
        Export
      </CustomButton>
    </Box>
  </Box>
);

const FiltersRow = ({
  selectedHighlightOption,
  interviewVariantType,
  duration,
  onDurationChange,
  minAttempts,
  onMinAttemptsChange,
  searchQuery,
  onSearchChange,
  searchResetCounter,
  minScore,
  onMinScoreChange,
  scoreBy,
  onScoreByChange,
  codingScore,
  onCodingScoreChange,
  codingScoreBy,
  onCodingScoreByChange,
}) => (
  <Box display='flex' justifyContent='space-between' width='100%'>
    <Box display='flex' gap='12px' alignItems='flex-end'>
      <SimpleSelect
        options={DurationOptions}
        value={duration._id}
        onSelect={onDurationChange}
        sx={{ width: '180px' }}
        label='Time duration'
      />
      {selectedHighlightOption === 'minimal_attempts' && (
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap="4px">
          <Box display="flex" alignItems="center" gap="4px">
            <Typography variant="body01-bold">Minimal attempts</Typography>
            <InfoIconWithTooltip title="Set the minimum number of attempts to highlight students (1-50)." />
          </Box>
          <TextField
            type="number"
            placeholder="Enter attempts (1-50)"
            value={minAttempts}
            onChange={onMinAttemptsChange}
            sx={{ width: '180px' }}
            inputProps={{ min: 1, max: 50 }}
            size="small"
          />
        </Box>
      )}
      <DebouncedTextField
        key={searchResetCounter}
        placeholder="Search..."
        sx={{ width: '220px' }}
        onChange={onSearchChange}
      />
      {selectedHighlightOption === 'lowest_scored' && (interviewVariantType === 'assessment' || interviewVariantType === 'verbal_mock' || interviewVariantType === 'verbal_iw') && (
        <>
          <Box display="flex" flexDirection="column" alignItems="flex-start" gap="4px">
            <Typography variant="body01-bold">Min Score</Typography>
            <TextField
              type="number"
              placeholder="Score (0-100)"
              value={minScore}
              onChange={onMinScoreChange}
              sx={{ width: '180px' }}
              inputProps={{ min: 0, max: 100 }}
              size="small"
            />
          </Box>
          <SimpleSelect
            options={ScoreCategoryOptions}
            value={scoreBy}
            onSelect={onScoreByChange}
            sx={{ width: '180px' }}
            label='Score By'
          />
        </>
      )}
      {selectedHighlightOption === 'lowest_scored' && (interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw') && (
        <>
          <Box display="flex" flexDirection="column" alignItems="flex-start" gap="4px">
            <Typography variant="body01-bold">Min Score</Typography>
            <TextField
              type="number"
              placeholder="Score (1-5)"
              value={codingScore}
              onChange={onCodingScoreChange}
              sx={{ width: '180px' }}
              inputProps={{ min: 1, max: 5 }}
              size="small"
            />
          </Box>
          <SimpleSelect
            options={CodingScoreCategoryOptions}
            value={codingScoreBy}
            onSelect={onCodingScoreByChange}
            sx={{ width: '180px' }}
            label='Score By'
          />
        </>
      )}
    </Box>
  </Box>
);

const HighlightsTableHeader = ({ selectedHighlightOption }) => (
  <Table>
    <TableHead sx={{ backgroundColor: '#f5f9fc' }}>
      <TableRow>
        <TableCell sx={{ height: '48px', padding: '8px 16px' }}>
          <Typography variant="body01-bold">Student name</Typography>
        </TableCell>
        <TableCell sx={{ height: '48px', padding: '8px 16px' }}>
          <Typography variant="body01-bold">Email</Typography>
        </TableCell>
        {selectedHighlightOption === 'unfinished_attempts' && (
          <TableCell sx={{ height: '48px', padding: '8px 16px' }}>
            <Typography variant="body01-bold">Total unfinished attempts</Typography>
          </TableCell>
        )}
        {!(selectedHighlightOption === 'pending_login' || selectedHighlightOption === 'no_attempts') && (
          <TableCell sx={{ height: '48px', padding: '8px 16px' }}>
            <Typography variant="body01-bold">Analytics</Typography>
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  </Table>
);

const BatchHighlighting = ({ batchId, interviewVariantType = InterviewVariantTypes.VERBAL_MOCK }) => {
    const classes = useStyles();
    const navigate = useNavigate();

    const [selectedHighlightOption, setSelectedHighlightOption] = useState(BatchHighlightingOptions[0]?._id || '');
    const [minAttempts, setMinAttempts] = useState(1);
    const [page, setPage] = useState(1);
    const [duration, setDuration] = useState(DurationOptions[4]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResetCounter, setSearchResetCounter] = useState(0);
    const [minScore, setMinScore] = useState(50);
    const [scoreBy, setScoreBy] = useState('overall');
    const [codingScore, setCodingScore] = useState(1);
    const [codingScoreBy, setCodingScoreBy] = useState('4c');
    const [highlightsData, setHighlightsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingRequests, setPendingRequests] = useState(0);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);

    const rowsPerPage = 10;
    const pageCount = Math.ceil(totalCount / rowsPerPage);

    const selectedOptionName = useMemo(
      () => BatchHighlightingOptions.find(opt => opt._id === selectedHighlightOption)?.name || '',
      [selectedHighlightOption]
    );

    useEffect(() => {
      const fetchHighlights = async () => {
        try {
          setPendingRequests(p => p + 1);
          setLoading(true);
          
          let highlightType = selectedHighlightOption;
          let additionalParams = {};
          
          if (selectedHighlightOption === 'minimal_attempts') {
            additionalParams.minAttempts = minAttempts;
          } else if (selectedHighlightOption === 'lowest_scored') {
            if (interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw') {
              additionalParams.minScore = codingScore;
              additionalParams.scoreBy = codingScoreBy;
            } else {
              additionalParams.minScore = minScore;
              additionalParams.scoreBy = scoreBy;
            }
          }

          const response = await getBatchHighlights(
            batchId, 
            interviewVariantType, 
            highlightType, 
            rowsPerPage, 
            page, 
            duration._id,
            additionalParams
          );

          setTotalCount(response?.total);
          const dataArray = Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
              ? response.data
              : [];

          setHighlightsData(dataArray);
          
          setError(null);
        } catch (err) {
          setError(err.message);
          console.error('Error fetching highlights:', err);
        } finally {
          setPendingRequests(p => {
            const next = Math.max(0, p - 1);
            if (next === 0) {
              setLoading(false);
            }
            return next;
          });
        }
      };

      if (batchId && selectedHighlightOption) {
        fetchHighlights();
      }
    }, [batchId, interviewVariantType, selectedHighlightOption, minAttempts,  minScore, scoreBy, codingScore, codingScoreBy, page, duration]);

    const handleHighlightOptionChange = (e) => {
      setSelectedHighlightOption(e.target.value);
      setPage(1);
      setSearchQuery('');
      setSearchResetCounter(c => c + 1);
    };

    const handleDurationChange = (e) => {
      const selectedDuration = e.target.value;
      setDuration(DurationOptions.find(opt => opt._id === selectedDuration));
      setPage(1);
      setSearchQuery('');
      setSearchResetCounter(c => c + 1);
    };

    const handleMinAttemptsChange = (e) => {
      const value = e.target.value;
      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= 1 && numValue <= 50) {
        setMinAttempts(numValue);
        setPage(1);
      } else if (value === '') {
        setMinAttempts('');
      }
      setSearchQuery('');
      setSearchResetCounter(c => c + 1);
    };

    const handleDecrement = () => {
      setMinAttempts((prev) => Math.max(1, prev - 1));
    };

    const handleIncrement = () => {
      setMinAttempts((prev) => Math.min(50, prev + 1));
    };

    const handleSearchChange = (value) => {
      setSearchQuery(value);
      setPage(1);
    };

    const handleMinScoreChange = (e) => {
      const value = e.target.value;
      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
        setMinScore(numValue);
        setPage(1);
      } else if (value === '') {
        setMinScore('');
      }
      setSearchQuery('');
      setSearchResetCounter(c => c + 1);
    };

    const handleScoreByChange = (e) => {
      setScoreBy(e.target.value);
      setPage(1);
      setSearchQuery('');
      setSearchResetCounter(c => c + 1);
    };

    const handleCodingScoreChange = (e) => {
      const value = e.target.value;
      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= 1 && numValue <= 5) {
        setCodingScore(numValue);
        setPage(1);
      } else if (value === '') {
        setCodingScore('');
      }
      setSearchQuery('');
      setSearchResetCounter(c => c + 1);
    };

    const handleCodingScoreByChange = (e) => {
      setCodingScoreBy(e.target.value);
      setPage(1);
      setSearchQuery('');
      setSearchResetCounter(c => c + 1);
    };

    const handleAnalyticsClick = (studentId) => {
      navigate(`/students/${studentId}`);
    };

    const handleExport = async () => {
      try {
        const exportColumns = [
          { field: 'name', label: 'Student name' },
          { field: 'email', label: 'Email' }
        ];

        let highlightType = selectedHighlightOption;
        let additionalParams = {};
        
        if (selectedHighlightOption === 'minimal_attempts') {
          additionalParams.minAttempts = minAttempts;
        } else if (selectedHighlightOption === 'lowest_scored') {
          if (interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw') {
            additionalParams.minScore = codingScore;
            additionalParams.scoreBy = codingScoreBy;
          } else {
            additionalParams.minScore = minScore;
            additionalParams.scoreBy = scoreBy;
          }
        }

        const response = await getBatchHighlights(
          batchId, 
          interviewVariantType, 
          highlightType, 
          null, 
          null, 
          duration._id,
          { ...additionalParams, type: 'all' }
        );

        const dataArray = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];

        const filteredExportData = dataArray.filter(item => 
          item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.email?.toLowerCase().includes(searchQuery.toLowerCase())
        );

        const fieldsToExport = ['name', 'email'];

        const highlightOptionName = BatchHighlightingOptions.find(opt => opt._id === selectedHighlightOption)?.name || 'batch-highlights';
        const filename = `${highlightOptionName.toLowerCase().replace(/\s+/g, '-')}-students`;

        exportToCsv(exportColumns, filteredExportData, fieldsToExport, filename);
      } catch (error) {
        console.error('Export failed:', error);
      }
    };

    const filteredData = highlightsData.filter(item => 
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
      return (
        <Box className={classes.container}>
          <HeaderControls
            selectedHighlightOption={selectedHighlightOption}
            onHighlightOptionChange={handleHighlightOptionChange}
            onExport={() => {}}
            isExportDisabled
          />
          <Box className={classes.tableContainer}>
            <Typography variant='h5-medium' color='primary.clr-500'>
              {selectedOptionName}
            </Typography>
            <FiltersRow
              selectedHighlightOption={selectedHighlightOption}
              interviewVariantType={interviewVariantType}
              duration={duration}
              onDurationChange={handleDurationChange}
              minAttempts={minAttempts}
              onMinAttemptsChange={handleMinAttemptsChange}
              onSearchChange={handleSearchChange}
          searchResetCounter={searchResetCounter}
              minScore={minScore}
              onMinScoreChange={handleMinScoreChange}
              scoreBy={scoreBy}
              onScoreByChange={handleScoreByChange}
              codingScore={codingScore}
              onCodingScoreChange={handleCodingScoreChange}
              codingScoreBy={codingScoreBy}
              onCodingScoreByChange={handleCodingScoreByChange}
            />
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
            <TableContainer component={Paper} sx={{ width: '100%', height: '400px' }}>
              <HighlightsTableHeader selectedHighlightOption={selectedHighlightOption} />
            </TableContainer>
          </Box>
        </Box>
      );
    }

    if (error) {
      return <BatchHighlightingEmptyState batchId={batchId} interviewVariantType={interviewVariantType} />;
    }

  return (
    <Box className={classes.container}>
        <HeaderControls
          selectedHighlightOption={selectedHighlightOption}
          onHighlightOptionChange={handleHighlightOptionChange}
          onExport={handleExport}
        />
        <Box className={classes.tableContainer}>
            <Typography variant='h5-medium' color='primary.clr-500'>
            {selectedOptionName}
            </Typography>
            <FiltersRow
              selectedHighlightOption={selectedHighlightOption}
              interviewVariantType={interviewVariantType}
              duration={duration}
              onDurationChange={handleDurationChange}
              minAttempts={minAttempts}
              onMinAttemptsChange={handleMinAttemptsChange}
              onSearchChange={handleSearchChange}
          searchResetCounter={searchResetCounter}
              minScore={minScore}
              onMinScoreChange={handleMinScoreChange}
              scoreBy={scoreBy}
              onScoreByChange={handleScoreByChange}
              codingScore={codingScore}
              onCodingScoreChange={handleCodingScoreChange}
              codingScoreBy={codingScoreBy}
              onCodingScoreByChange={handleCodingScoreByChange}
            />
          
            <TableContainer component={Paper} sx={{ width: '100%', height: '400px' }}>
                {filteredData.length === 0 ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={240}>
                        <Box mb={2}>
                            <img
                                src='https://assets.languify.in/images/no_data.png'
                                alt='no data'
                                width={151}
                                height={97}
                            />
                        </Box>
                        <Typography variant="h6-medium">No data available</Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f5f9fc' }}>
                          <TableRow>
                            <TableCell sx={{ height: '48px', padding: '8px 16px' }}>
                              <Typography variant="body01-bold">Student name</Typography>
                            </TableCell>
                            <TableCell sx={{ height: '48px', padding: '8px 16px' }}>
                              <Typography variant="body01-bold">Email</Typography>
                            </TableCell>
                            {selectedHighlightOption === 'unfinished_attempts' && (
                              <TableCell sx={{ height: '48px', padding: '8px 16px' }}>
                                <Typography variant="body01-bold">Total unfinished attempts</Typography>
                              </TableCell>
                            )}
                            {!(selectedHighlightOption === 'pending_login' || selectedHighlightOption === 'no_attempts') && (
                              <TableCell sx={{ height: '48px', padding: '8px 16px' }}>
                                <Typography variant="body01-bold">Analytics</Typography>
                              </TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((row, idx) => (
                            <TableRow key={row._id || row.id || idx}>
                                <TableCell sx={{ height: '40px', padding: '8px 16px' }}>
                                <Typography variant="body01-medium">{row.name || 'N/A'}</Typography>
                                </TableCell>
                                <TableCell sx={{ height: '40px', padding: '8px 16px' }}>
                                <Typography variant="body01-medium">{row.email || 'N/A'}</Typography>
                                </TableCell>
                                {selectedHighlightOption === 'unfinished_attempts' && (
                                    <TableCell sx={{ height: '40px', padding: '8px 16px' }}>
                                        <Typography variant="body01-medium">{row.unfinishedAttempts || 0}</Typography>
                                    </TableCell>
                                )}    
                                {!(selectedHighlightOption === 'pending_login' || selectedHighlightOption === 'no_attempts') && (
                                  <TableCell sx={{ height: '40px', padding: '8px 16px' }}>
                                    <Box 
                                      onClick={() => handleAnalyticsClick(row._id)}
                                      sx={{ 
                                        cursor: 'pointer', 
                                        display: 'inline-flex',
                                        '&:hover': {
                                          opacity: 0.7
                                        }
                                      }}
                                    >
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="4" y="14" width="3" height="6" rx="1" fill="#1976D2"/><rect x="10.5" y="10" width="3" height="10" rx="1" fill="#1976D2"/><rect x="17" y="6" width="3" height="14" rx="1" fill="#1976D2"/></svg>
                                    </Box>
                                  </TableCell>
                                )}
                            </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            {filteredData.length > 0 && (
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                    <Typography variant="body01-medium">
                        Students: {(page - 1) * rowsPerPage + 1}-{Math.min(page * rowsPerPage, totalCount)} of {totalCount}
                    </Typography>
                    <Pagination count={pageCount} page={page} setPage={setPage} />
                </Box>
            )}
        </Box>
    </Box>
  )
}

export default BatchHighlighting